import { createApiClient, ApiClientError } from '@warmancer/ui';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = createApiClient({
  baseUrl: API_BASE_URL,
  onUnauthorized: () => {
    useAuthStore.getState().clearSession();
  },
  getAuthToken: () => useAuthStore.getState().sessionToken,
});

export { ApiClientError };

export const DISCORD_URL = 'https://discord.gg/zDSfXWrDM8';

/**
 * Maps API error codes to user-friendly messages.
 * Use \n between sentences to enable line-break rendering in popups.
 */
export const errorMessages: Record<string, string> = {
  // Auth errors
  AUTH_INVALID_CREDENTIALS: 'Invalid name/email or password.\nPlease check your credentials.',
  AUTH_ACCOUNT_LOCKED: 'Your account is temporarily locked due to too many failed login attempts.\nPlease reset your password.',
  AUTH_SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  AUTH_INVALID_TOKEN: 'This link is invalid or has expired. Please request a new one.',
  AUTH_TOKEN_EXPIRED: 'This link has expired. Please request a new one.',
  AUTH_CAPTCHA_REQUIRED: 'Please complete the CAPTCHA verification.',
  AUTH_EMAIL_NOT_VERIFIED: 'Please verify your email address before logging in.\nCheck your inbox and spam folder for a verification link from contact@warmancer.com.\nJoin us on Discord for help.',

  // Account errors
  ACCOUNT_BANNED: 'This account has been banned.\nContact support for more information.',
  ACCOUNT_DELETED: 'This account has been deleted.',
  ACCOUNT_NOT_FOUND: 'Account not found.',

  // Registration errors
  USERNAME_TAKEN: 'This name is already taken. Please choose another.',
  USERNAME_INVALID: 'Name must be 3-16 characters and contain only letters and numbers.',
  USERNAME_RESERVED: 'This name is reserved and cannot be used.',
  USERNAME_OFFENSIVE: 'This name is not allowed. Please choose another.',
  EMAIL_TAKEN: 'This email is already registered.\nTry logging in or use a different email.',
  EMAIL_INVALID: 'Please enter a valid email address.',
  EMAIL_DOMAIN_NOT_ALLOWED: 'This email domain is not allowed. Please use a different email.',
  PASSWORD_TOO_SHORT: 'Password must be at least 7 characters.',

  // Rate limiting
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',

  // Validation
  VALIDATION_ERROR: 'Please check your input and try again.',

  // Network
  PARSE_ERROR: 'Unable to connect to the server.\nPlease check your internet connection.',
  HTTP_ERROR: 'Something went wrong. Please try again.',

  // Device verification
  DEVICE_ALREADY_REGISTERED: 'This device has already been used to create an account.\nOnly one account per device is allowed.',
  DEVICE_ACCOUNT_MISMATCH: 'This device is registered to a different account.\nEach device can only access one account.',
  VPN_DETECTED: 'VPN or proxy detected.\nPlease disable your VPN to continue.',
  FINGERPRINT_REQUIRED: 'Device verification is required. Please refresh the page and try again.',
  FINGERPRINT_VERIFICATION_FAILED: 'Unable to verify your device.\nPlease try again.',
  BOT_DETECTED: 'Automated access detected.\nPlease disable any automation tools and try again.',
  TAMPERING_DETECTED: 'Browser tampering detected.\nPlease disable ad blocking, allow scripts, disable VPNs, and refresh the page.\nIf errors persist, clear website cookies and cache.',
  LOW_CONFIDENCE: 'Unable to verify your device.\nPlease disable ad blocking, allow scripts, disable VPNs, and refresh the page.\nIf errors persist, clear website cookies and cache.',
  TOR_DETECTED: 'Tor network detected.\nPlease disable Tor to continue.',
  PROXY_DETECTED: 'Proxy detected.\nPlease disable your proxy to continue.',
  IP_BLOCKLISTED: 'Your IP address is on a blocklist.\nPlease try from a different network.',
  VIRTUAL_MACHINE_DETECTED: 'Virtual machine detected.\nPlease use a physical device.',
  SUSPECT_SCORE_HIGH: 'Your device has been flagged as suspicious.\nPlease disable ad blocking, allow scripts, disable VPNs, and refresh the page.\nIf errors persist, clear website cookies and cache.',
  INCOGNITO_DETECTED: 'Private/incognito browsing detected.\nPlease use a regular browser window.',
  DEVELOPER_TOOLS_DETECTED: 'Developer automation tools detected.\nPlease disable any browser automation software.',
  REMOTE_CONTROL_DETECTED: 'Remote control software detected.\nPlease disable remote access tools.',
  LOCATION_SPOOFING_DETECTED: 'Location spoofing detected.\nPlease disable GPS spoofing tools.',
  EMULATOR_DETECTED: 'Emulator detected.\nPlease use a physical device.',
  FRIDA_DETECTED: 'Frida detected.\nPlease disable any hooking tools.',
  CLONED_APP_DETECTED: 'Cloned app detected.\nPlease use the official application.',

  // Service unavailable
  EMAIL_SERVICE_UNAVAILABLE: 'Account registration is temporarily unavailable.',

  // Default
  INTERNAL_ERROR: 'Something went wrong on our end.\nPlease try again later.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

/**
 * Gets a user-friendly error message for an API error
 */
export function getErrorMessage(error: Error): string {
  if (error instanceof ApiClientError) {
    return errorMessages[error.code] || error.message;
  }
  return error.message || 'An unexpected error occurred';
}

// Settings API
export interface SettingsEnvelope {
  settings: Record<string, unknown>;
  lastSavedAt: string;
  clientVersion: string;
}

export const settingsApi = {
  /** Load player settings. Returns { data: SettingsEnvelope | null }. */
  load: () =>
    api.get<{ data: SettingsEnvelope | null }>('/settings'),

  /** Save player settings. Returns { lastSavedAt: string }. */
  save: (settings: Record<string, unknown>, clientVersion: string) =>
    api.put<{ lastSavedAt: string }>('/settings', { settings, clientVersion }),
};

// Particle defaults API
export const particleDefaultsApi = {
  /** Load global particle effect defaults. */
  load: () =>
    api.get<{ defaults: Record<string, unknown> }>('/particle-defaults'),

  /** Save a single effect's default config (Ozymandias only). */
  save: (effectId: string, config: Record<string, unknown>) =>
    api.put<{ effectId: string; saved: boolean }>(`/particle-defaults/${effectId}`, config),
};

// Auth API types
export interface LoginRequest {
  username_or_email: string;
  password: string;
  turnstile_token?: string;
  fp_request_id: string;
}

export interface LoginResponse {
  session_token: string;
  account: {
    id: string;
    username: string;
    email: string;
    email_verified: boolean;
    avatar_url: string | null;
    status: string;
    created_at: string;
  };
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  referral?: string;
  turnstile_token: string;
  fp_request_id: string;
}

export interface RegisterResponse {
  account_id: string;
  verification_required: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
  turnstile_token?: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface CheckUsernameResponse {
  available: boolean;
  error?: string;
}

export interface ForgotPasswordResponse {
  rate_limited?: boolean;
  retry_after?: number;
}

// Auth API functions
export const authApi = {
  login: (data: LoginRequest) =>
    api.post<LoginResponse>('/auth/login', data),

  register: (data: RegisterRequest) =>
    api.post<RegisterResponse>('/auth/register', data),

  logout: () =>
    api.post<void>('/auth/logout'),

  forgotPassword: (data: ForgotPasswordRequest) =>
    api.post<void>('/auth/forgot-password', data),

  resetPassword: (data: ResetPasswordRequest) =>
    api.post<void>('/auth/reset-password', data),

  verifyEmail: (data: VerifyEmailRequest) =>
    api.post<void>('/auth/verify-email', data),

  resendVerification: () =>
    api.post<void>('/auth/resend-verification'),

  resendVerificationPublic: (data: { email: string }) =>
    api.post<void>('/auth/resend-verification-public', data),

  getSession: () =>
    api.get<LoginResponse>('/auth/session'),

  checkUsername: (username: string) =>
    api.get<CheckUsernameResponse>(`/auth/check-username?username=${encodeURIComponent(username)}`),

  forgotPasswordWithRateLimit: (data: ForgotPasswordRequest) =>
    api.post<ForgotPasswordResponse>('/auth/forgot-password', data),
};
